import { RouteComponentProps } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import { BrokerModel, brokerModuleEditRoles, EditBrokerRequest } from '../../../../api/admin/admin.types';
import React, { FormEvent } from 'react';
import { AppState } from '../../../../store/store.types';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { FieldControl, FieldGroup, FormBuilder } from 'react-reactive-form';
import { StatusType } from '../../../../api/api.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { UserModel } from '../../../../api/authentication/authentication.types';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { TextInput } from '../../../../components/forms/Input';
import { BrokerActionTypes } from '../../types/broker.types';
import { addNewBroker, deleteBroker, editBroker, getBroker } from '../../actions/broker.actions';
import { InputTextArea } from '../../../../components/forms/TextArea';
import Checkbox from '../../../../components/forms/Checkbox';
import StatusLabel from '../../../../components/forms/StatusLabel';

interface BrokerRouteProps {
  id?: string;
}

interface BrokerDetailProps extends RouteComponentProps<BrokerRouteProps>, WithTranslation {
  selectedBroker: BrokerModel | null;
  getSelectedBroker: (id: number) => void;
  editBroker: (id: number, request: EditBrokerRequest) => void;
  addNewBroker: (request: EditBrokerRequest) => void;
  deleteBroker: (id: number) => Promise<void>;
  isNew?: boolean;
  postSuccess: boolean | null;
  user: UserModel | null;
}

interface BrokerDetailState {
  isModalOpen: boolean;
  isEdit: boolean;
}

class BrokerDetails extends React.Component<BrokerDetailProps, BrokerDetailState> {
  readonly state = {
    isModalOpen: false,
    isEdit: false
  };

  form = FormBuilder.group({
    name: [ '' ],
    legalName: [ '' ],
    comment: [ '' ],
    statusType: [ '' ],
    active: [ true ],
  });

  componentDidMount() {
    const { id } = this.props.match.params;

    if (id) {
      this.props.getSelectedBroker(Number(id));
    }

  }

  componentDidUpdate(prevProps: BrokerDetailProps, prevState: BrokerDetailState, snapshot?: any) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedBroker) {
      this.props.history.push(`/admin/manage-brokers/${this.props.selectedBroker.id}/view`);
    }
    if (this.props.selectedBroker !== prevProps.selectedBroker) {
      this.updateFormValue();

    }
  }

  updateFormValue = () => {
    if (!this.props.selectedBroker) {
      return;
    }
    this.form.patchValue({
      name: this.props.selectedBroker.name,
      legalName: this.props.selectedBroker.legalName,
      comment: this.props.selectedBroker.comment,
      active: this.props.selectedBroker.statusType === StatusType.ACTIVE,
    });
  };

  onDeleteConfirm = () => {
    const { id } = this.props.match.params;
    this.props.deleteBroker(Number(id))
      .then(() => {
        this.props.history.push('/admin/manage-brokers/');
      });
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }
    const request: EditBrokerRequest = {
      name: form.name.value,
      comment: form.comment.value,
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
      legalName: form.legalName.value
    };
    if (this.props.selectedBroker) {
      const { id } = this.props.match.params;
      this.props.editBroker(Number(id), request);
      this.setState({ isEdit: false });
    } else {
      this.props.addNewBroker(request);
    }
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/admin/brokers/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  getBrokerName = () => {
    const { t } = this.props;
    return (
      <div className="form-group d-flex">
        <div className="col-4 d-flex align-items-center justify-content-end">
          {(this.state.isEdit || this.props.isNew) && (
            <label className="mandatory-field">* </label>
          )}
          <label>{t('l.broker.name')}: </label>
        </div>
        <div className="col-4 d-flex align-items-center justify-content-start edit-form-input">
          {this.props.selectedBroker && !(this.state.isEdit || this.props.isNew) && (
            <div className="form-control-static">
              {this.props.selectedBroker.name}
            </div>
          )}
          {(this.props.isNew || this.state.isEdit) && (
            <FieldControl
              name="name"
              render={TextInput}
            />
          )}
        </div>
      </div>
    );
  };

  getLegalName = () => {
    const { t } = this.props;
    return (
      <div className="form-group d-flex">
        <div className="col-4 d-flex align-items-center justify-content-end">
          {(this.state.isEdit || this.props.isNew) && (
            <label className="mandatory-field">* </label>
          )}
          <label>{t('l.broker.legal_name')}: </label>
        </div>
        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
          {this.props.selectedBroker && !(this.state.isEdit || this.props.isNew) && (
            <div className="form-control-static">
              {this.props.selectedBroker.legalName}
            </div>
          )}
          {(this.props.isNew || this.state.isEdit) && (
            <FieldControl
              name="legalName"
              render={TextInput}
            />
          )}
        </div>
      </div>
    );
  };

  getStatus = () => {
    const { t } = this.props;
    return (
      <div className="form-group d-flex">
        <div className="col-4 d-flex align-items-center justify-content-end">
          {(this.state.isEdit || this.props.isNew) && (
            <label className="mandatory-field">* </label>
          )}
          <label>{t('l.broker.status_type')}: </label>
        </div>
        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
          {this.props.selectedBroker && !(this.state.isEdit || this.props.isNew) && (
            <div className="form-control-static">
              <StatusLabel status={this.props.selectedBroker.statusType}></StatusLabel>
            </div>
          )}
          {(this.props.isNew || this.state.isEdit) && (
            <FieldControl
              name="active"
              render={(props) => <Checkbox {...props} id="statusField" className="mt-1">Active</Checkbox>}
            />
          )}
        </div>
      </div>
    );
  };

  getComment = () => {
    const { t } = this.props;
    return (
      <div className="form-group d-flex">
        <div className="col-4 d-flex align-items-center justify-content-end">
          <label>{t('l.broker.comment')}: </label>
        </div>
        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
          {this.props.selectedBroker && !(this.state.isEdit || this.props.isNew) && (
            <div className="form-control-static">
              {this.props.selectedBroker.comment}
            </div>
          )}
          {(this.props.isNew || this.state.isEdit) && (
            <FieldControl
              name="comment"
              strict={false}
              render={(props) => (
                  <InputTextArea
                    className="d-flex align-items-center form-input justify-content-start"
                    {...props}
                  />)
              }
            />
          )}
        </div>
      </div>
    );
  };


  getButtons = () => {
    const { t } = this.props;
    if (this.state.isEdit || this.props.isNew) {
      return (
        <div className="row">
          <div className="col-12 text-right">
            <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                    onClick={this.onCancel}>
              {t('b.cancel_Generic cancel button translation').toUpperCase()}
            </button>
            <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
              {t('b.save_Generic save button translation').toUpperCase()}
            </button>
          </div>
        </div>
      );
    }
  };

  render() {
    if (!this.props.isNew && !this.props.selectedBroker) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNew;

    const { t } = this.props;


    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/admin', label: 'Admin' },
              { path: '/admin/manage-brokers', label: 'Manage brokers' },
              {
                path: null,
                label: this.props.isNew ? 'New broker' : this.props.selectedBroker?.name || ''
              }
            ]
          }
        />
        <div className="page-content">
          {this.props.user && userCanAccessComponent(this.props.user, brokerModuleEditRoles) && (
            <>
              <div className="row justify-content-end">
                <div className="col-12 d-flex align-items-center justify-content-between">
                  <h1
                    className="mb-0">{this.props.isNew || !this.props.selectedBroker ? 'Add new broker' : this.props.selectedBroker.name}</h1>
                </div>
              </div>
              <div>
                <div className="mb-2"/>
                {this.props.selectedBroker && !showForm && (
                  <SectionTitle
                    title="DETAILS"
                    showButton
                    buttonName={t('b.edit_Generic edit button').toUpperCase()}
                    showDeleteButton
                    deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
                    onDeleteClick={() => this.setState({ isModalOpen: true })}
                    onEditClick={() => this.onEdit()}
                  />
                )}
                {this.props.selectedBroker && showForm && (
                  <SectionTitle title="DETAILS"
                  />
                )}
              </div>
              <FieldGroup
                strict={false}
                control={this.form}
                render={() => (
                  <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                    {this.getBrokerName()}
                    {this.getLegalName()}
                    {this.getComment()}
                    {this.getStatus()}
                    {this.getButtons()}
                  </form>
                )}
              />
            </>)}
        </div>
        <ConfirmationModal
          title="Confirm broker removal"
          content="Are you sure you want to remove this broker?"
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedBroker: state.admin.brokers.selectedBroker,
  brokers: state.admin.brokers.brokers,
  postSuccess: state.admin.brokers.postSuccess,
  lastFetch: state.admin.brokers.lastFetch,
  activeSupplier: state.authentication.activeSupplier,
  user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, BrokerActionTypes>
) => ({
  getSelectedBroker: (id: number) => dispatch(getBroker(id)),
  editBroker: (id: number, request: EditBrokerRequest) => dispatch(editBroker(id, request)),
  addNewBroker: (request: EditBrokerRequest) => dispatch(addNewBroker(request)),
  deleteBroker: (id: number) => dispatch(deleteBroker(id))
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(BrokerDetails));